import { FieldConfig } from '@core/components/crudbuilder/models/field-config';
import { ErrorFormatter } from '@core/helpers/error-formatter';
import { of } from 'rxjs';
import { BreadBuilderService } from '@core/services/bread-builder/bread-builder.service';
import { PopularAppStatesService } from '@core/services/popular-app-states/popular-app-states.service';
import { FieldGroup } from '@core/pages/crud/dependent-fields/field-group';

/**
 * Config
 * Used on both the generic CRUDs and the Wizard CRUD.
 */
export class CrudBuilderConfig {
  /**
   * These are basically used for the Title for the pages generated from the config data.
   */
  singular?: string;
  plural?: string;
  /**
   * The model alias for this CRUDBuilder
   * i.e. home, person, rental-home, vehicle
   * It is used for stateService.getQuery and getService on BREAD edit/add forms.
   */
  modelAlias?: any;
  path?: string;
  title?: string;
  customTitle?: string;
  hasCustomizedEditTitle: boolean;

  /**
   * Used to determine check status in the top bar for the Wizard Page.
   */
  checked: boolean;

  /**
   * A field can be a string or a FieldConfig.
   * It it is a string, it will check the model what data type it has, and then converts it to a FieldConfig.
   */
  fields: Array<string | FieldConfig | FieldGroup>;

  /**
   * If there is no readFields content, basically just use the fields array.
   */
  readFields?: Array<string | FieldConfig | FieldGroup>;

  /**
   * Model Data
   * Basically used on wizard page to store values that should be shown or saved.
   */
  model?: any = {};
  /**
   * You can put string 'CUSTOM' to use the customGet() function.
   */
  getEndpoint?: string;
  /**
   * You can put string 'CUSTOM' to use the customGet() function.
   */
  putEndpoint?: string;

  errors = new ErrorFormatter();

  /**
   * This is for multiEdit fields.
   * It contains the errors for each field.
   */
  manyErrors: Array<ErrorFormatter> = [];

  /**
   * Usually used for wizard-based CrudBuilders.
   * It means that you can edit more than one model within 1 form.
   */
  multiEdit = false;
  models    = [];

  /**
   * ion-icon to use as the icon for this config.
   */
  icon = 'user';

  /**
   * This is what will be seen on the list view.
   * Basically something HTML.
   * Example:
   * ```
   * const view = new ListFormatter()
   *   .fHead('Address', Formatter.formatAddress(model.address))
   *   .fSub('Price', Formatter.formatMoney(model.price))
   *   .fSub('Woz', Formatter.formatMoney(model.woz));
   *
   * return view.toString();
   * ```
   */
  listView(model: any): string | null {
    return model.id;
  }

  getModel() {
    return {};
  }

  getEditTitle(model) {
    return 'Edit Title';
  }

  getReadTitle(model) {
    return 'Read Title';
  }

  runOnLoad(s: BreadBuilderService) {
    return of({});
  }

  /**
   * Preprocess fields so they are ready for the edit view.
   *
   * For example, email is in 'user.email' field on the model.
   * But saving it needs to be on the 'email' field.
   *
   * Another example, nationality is in nationality.id,
   * But saving it needs to be on the 'nationality_configuration_id' field.
   */
  editPreprocess(model) {
    return model;
  }

  runBeforeSavingOnEditView(model, s: PopularAppStatesService = null) {
    return model;
  }

  runBeforeSavingOnAddView(model, states) {
    return model;
  }

  runBeforeSaving(model, s: BreadBuilderService) {
    return model;
  }

  /**
   * Custom GET Endpoint that automatically sets the model.
   * It should return a Observable<model>, not an array.
   */
  customGet(s: BreadBuilderService) {
    return of({});
  }

  /**
   * Custom SET Endpoint that automatically sets the model.
   * It should return a Observable<model>, not an array
   */
  customPut(s: BreadBuilderService) {
    return of({});
  }

  /**
   * Custom Back Links Support
   * Useful for custom read/browse views.
   * Basically the back button goes to here instead of the default read and browse views on edit and add
   * CrudBuilder pages.
   */
  hasCustomBackRoute = false;
  getCustomBackRoute() {
    return '';
  }
}
